import { useState, useCallback, useEffect } from 'react';
/**
 * A custom hook that provides responsive screen information based on the window's width.
 *
 * @returns An object containing:
 * - `isMobile`: A boolean indicating whether the screen width is less than 992 pixels.
 * - `isDesktop`: A boolean indicating whether the screen width is 992 pixels or greater.
 *
 * @remarks
 * - The hook initializes `isMobile` based on the current window width.
 * - It listens for the `resize` event to update the screen size dynamically.
 * - The threshold for determining mobile screens is set to 992 pixels.
 *
 * @example
 * ```tsx
 * const { isMobile, isDesktop } = useResponsiveScreen();
 *
 * return (
 *   <div>
 *     {isMobile ? 'Mobile View' : 'Desktop View'}
 *   </div>
 * );
 * ```
 */
export const useResponsiveScreen = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const updateScreenSize = useCallback(() => {
        setIsMobile(window.innerWidth < 992);
    }, []);
    useEffect(() => {
        window.addEventListener('resize', updateScreenSize);
        return () => {
            window.removeEventListener('resize', updateScreenSize);
        };
    }, [updateScreenSize]);
    return { isMobile, isDesktop: !isMobile };
};
